import { useCallback, useState } from 'react';
import {
    SongOptions,
    UdioResponse,
    CompleteSequenceOptions,
    SequenceResponse,
    SongStatusResponse,
    UseUdioOptions,
    UseUdioResult
} from './types';
import { UdioWrapper } from './core/wrapper';

// react hook for using the Udio wrapper
export function useUdio(
    client: UdioWrapper,
    options: UseUdioOptions = {}
): UseUdioResult {
    const [isLoading, setIsLoading] = useState(false);

    const createSong = useCallback(async (songOptions: SongOptions): Promise<UdioResponse> => {
        setIsLoading(true);

        try {
            const result = await client.createSong(songOptions);

            if (options.autoPolling && result.status === 'processing') {
                const completed = await client.waitForCompletion(result.id, {
                    pollingInterval: options.pollingInterval,
                    maxAttempts: options.maxPollingAttempts,
                });

                return {
                    ...result,
                    status: completed.status,
                    url: completed.url || result.url,
                };
            }

            return result;
        } finally {
            setIsLoading(false);
        }
    }, [client, options.autoPolling, options.pollingInterval, options.maxPollingAttempts]);

    const extendSong = useCallback(async (songOptions: SongOptions): Promise<UdioResponse> => {
        setIsLoading(true);

        try {
            const result = await client.extendSong(songOptions);

            if (options.autoPolling && result.status === 'processing') {
                const completed = await client.waitForCompletion(result.id, {
                    pollingInterval: options.pollingInterval,
                    maxAttempts: options.maxPollingAttempts,
                });

                return {
                    ...result,
                    status: completed.status,
                    url: completed.url || result.url,
                };
            }

            return result;
        } finally {
            setIsLoading(false);
        }
    }, [client, options.autoPolling, options.pollingInterval, options.maxPollingAttempts]);

    const addOutro = useCallback(async (songOptions: SongOptions): Promise<UdioResponse> => {
        setIsLoading(true);

        try {
            const result = await client.addOutro(songOptions);

            if (options.autoPolling && result.status === 'processing') {
                const completed = await client.waitForCompletion(result.id, {
                    pollingInterval: options.pollingInterval,
                    maxAttempts: options.maxPollingAttempts,
                });

                return {
                    ...result,
                    status: completed.status,
                    url: completed.url || result.url,
                };
            }

            return result;
        } finally {
            setIsLoading(false);
        }
    }, [client, options.autoPolling, options.pollingInterval, options.maxPollingAttempts]);

    const createCompleteSequence = useCallback(async (
        sequenceOptions: CompleteSequenceOptions
    ): Promise<SequenceResponse> => {
        setIsLoading(true);

        try {
            return await client.createCompleteSequence(sequenceOptions);
        } finally {
            setIsLoading(false);
        }
    }, [client]);

    const getSongStatus = useCallback(async (songId: string): Promise<SongStatusResponse> => {
        setIsLoading(true);

        try {
            return await client.getSongStatus(songId);
        } finally {
            setIsLoading(false);
        }
    }, [client]);

    return {
        isLoading,
        createSong,
        extendSong,
        addOutro,
        createCompleteSequence,
        getSongStatus,
    };
}